import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../lib/api';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  TrendingUp,
  DollarSign,
  Wallet,
  Percent,
  AlertCircle,
  CheckCircle,
  Package,
  RefreshCw,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { downloadFile } from '../../lib/download';
import { fmt, KpiCard, PAYMENT_STATUS_LABELS } from './shared';
import EmptyState from '../../components/EmptyState';

// ─────────────────────────────────────────────────────────────────────────────
// Finance Summary KPIs (shared across sub-pages)
// ─────────────────────────────────────────────────────────────────────────────
const FinanceSummaryKpis = ({ summary }) => {
  const { t } = useLanguage();
  if (!summary) return null;
  const profitColor = summary.total_profit > 0 ? 'green' : 'red';
  const marginColor = summary.margin_pct >= 20 ? 'green' : summary.margin_pct > 0 ? 'amber' : 'red';
  const debtColor = summary.total_debt > 0 ? 'amber' : 'slate';
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
      <KpiCard icon={DollarSign} label={t('totalRevenue')} value={fmt(summary.total_revenue)} sub={`${summary.shipment_count} ${t('shipments')}`} color="blue" />
      <KpiCard icon={Wallet} label={t('totalCharges')} value={fmt(summary.total_charges)} color="orange" />
      <KpiCard icon={TrendingUp} label={t('netProfit')} value={fmt(summary.total_profit)} color={profitColor} />
      <KpiCard icon={Percent} label={t('marginPercent')} value={`${summary.margin_pct}%`} color={marginColor} />
      <KpiCard icon={AlertCircle} label={t('debt')} value={fmt(summary.total_debt)} color={debtColor} />
      <KpiCard icon={CheckCircle} label={t('profitableOnes')} value={`${summary.profitable_count} / ${summary.shipment_count}`} color="slate" />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Partner Finance View (standalone, no sub-navigation)
// ─────────────────────────────────────────────────────────────────────────────
const PartnerFinanceView = () => {
  const { t, language } = useLanguage();
  const paymentLabels = (PAYMENT_STATUS_LABELS[language] || PAYMENT_STATUS_LABELS.fr);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/api/partner/finance-summary');
        setData(res.data);
      } catch (err) {
        console.error('Error fetching partner finance:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </ProfessionalLayout>
    );
  }

  const stats = data?.stats || {};
  const packages = data?.packages || [];

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <TrendingUp size={24} className="text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-bold">{t('finance')}</h1>
            <p className="text-muted-foreground text-sm">
              {language === 'fr' ? 'Synthèse de vos collectes et paiements' : 'Summary of your collections and payments'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">
                  {language === 'fr' ? 'Colis collectés' : 'Packages collected'}
                </span>
                <Package size={18} className="text-blue-500" />
              </div>
              <div className="text-2xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{stats.total_packages}</div>
              <p className="text-xs text-muted-foreground mt-1">{stats.total_weight} kg</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">
                  {language === 'fr' ? 'Chiffre d\'affaires' : 'Revenue'}
                </span>
                <DollarSign size={18} className="text-emerald-500" />
              </div>
              <div className="text-2xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(stats.total_revenue)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">
                  {language === 'fr' ? 'Encaissé' : 'Collected'}
                </span>
                <CheckCircle size={18} className="text-green-500" />
              </div>
              <div className="text-2xl font-bold text-green-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(stats.paid_revenue)}</div>
              <p className="text-xs text-muted-foreground mt-1">{stats.paid_count} {language === 'fr' ? 'payés' : 'paid'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">
                  {language === 'fr' ? 'Restant dû' : 'Outstanding'}
                </span>
                <AlertCircle size={18} className="text-red-500" />
              </div>
              <div className="text-2xl font-bold text-red-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(stats.outstanding)}</div>
              <p className="text-xs text-muted-foreground mt-1">{stats.unpaid_count} {language === 'fr' ? 'impayés' : 'unpaid'}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-4">
            <h2 className="text-lg font-semibold mb-4">
              {language === 'fr' ? 'Mes colis collectés' : 'My collected packages'}
            </h2>
            {packages.length === 0 ? (
              <EmptyState
                icon={Package}
                title={language === 'fr' ? 'Aucun colis' : 'No packages'}
                description={language === 'fr' ? 'Vous n\'avez pas encore collecté de colis' : 'You haven\'t collected any packages yet'}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Code</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">{language === 'fr' ? 'Expéditeur' : 'Sender'}</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">{language === 'fr' ? 'Destinataire' : 'Recipient'}</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">{language === 'fr' ? 'Poids' : 'Weight'}</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">{language === 'fr' ? 'Montant' : 'Amount'}</th>
                      <th className="text-center px-3 py-2 font-medium text-muted-foreground">{language === 'fr' ? 'Paiement' : 'Payment'}</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {packages.map(pkg => {
                      const ps = paymentLabels[pkg.payment_status] || paymentLabels.unpaid;
                      return (
                        <tr key={pkg.id} className="hover:bg-muted/30">
                          <td className="px-3 py-2 font-mono text-xs">{pkg.oce_code}</td>
                          <td className="px-3 py-2">{pkg.sender_name}</td>
                          <td className="px-3 py-2">{pkg.recipient_name}</td>
                          <td className="px-3 py-2 text-right">{pkg.weight} kg</td>
                          <td className="px-3 py-2 text-right font-medium">{fmt(pkg.total_price || pkg.price)}</td>
                          <td className="px-3 py-2 text-center">
                            <Badge className={`text-[10px] ${ps.cls}`}>{ps.label}</Badge>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="secondary" className="text-[10px]">
                              {pkg.status?.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ProfessionalLayout>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-Navigation Tabs
// ─────────────────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { path: 'shipments', labelKey: 'byShipment' },
  { path: 'annual', labelKey: 'annualView' },
  { path: 'invoices', labelKey: 'invoices' },
  { path: 'pricing', labelKey: 'pricing' },
  { path: 'analysis', labelKey: 'analysis' },
];

const FinanceSubNav = () => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-1 border-b border-border pb-0">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.path}
          to={`/finance/${item.path}`}
          className={({ isActive }) =>
            `px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              isActive
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`
          }
        >
          {t(item.labelKey)}
        </NavLink>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FinanceLayout (admin/company_admin wrapper)
// ─────────────────────────────────────────────────────────────────────────────
const FinanceLayout = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isInvoicesPage = location.pathname.endsWith('/invoices');
  const [summary, setSummary] = useState(null);

  const handleSummaryLoaded = useCallback((s) => setSummary(s), []);

  useEffect(() => {
    if (user && !['company_admin', 'super_admin', 'partner'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (!user) return null;

  if (user.role === 'partner') return <PartnerFinanceView />;

  if (!['company_admin', 'super_admin'].includes(user.role)) return null;

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <TrendingUp size={24} className="text-muted-foreground" />
            <div>
              <h1 className="text-2xl font-bold">{t('finance')}</h1>
              <p className="text-muted-foreground text-sm">{t('financeSubtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const year = new Date().getFullYear();
                  await downloadFile(
                    `/api/reports/finance/excel`,
                    `finance_${year}.xlsx`,
                    { year }
                  );
                } catch {}
              }}
            >
              <FileSpreadsheet size={14} className="mr-1" />
              Export Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const now = new Date();
                  const start = `${now.getFullYear()}-01-01`;
                  const end = now.toISOString().slice(0, 10);
                  await downloadFile(
                    `/api/reports/finance/pdf`,
                    `rapport_financier_${start}_${end}.pdf`,
                    { start_date: start, end_date: end }
                  );
                } catch {}
              }}
            >
              <FileText size={14} className="mr-1" />
              Export PDF
            </Button>
          </div>
        </div>

        {/* KPI Summary Cards — hidden on invoices page (has its own KPIs) */}
        {!isInvoicesPage && <FinanceSummaryKpis summary={summary} />}

        {/* Sub-navigation */}
        <FinanceSubNav />

        {/* Sub-page content */}
        <Outlet context={{ onSummaryLoaded: handleSummaryLoaded }} />
      </div>
    </ProfessionalLayout>
  );
};

export default FinanceLayout;
